import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AttentionItem, HomeResponse, ProjectRow } from '../api/client.js';
import { fetchHome } from '../api/client.js';
import { CreateProjectForm } from '../components/CreateProjectForm.js';
import { StateChip } from '../components/StateChip.js';
import { useDictionary } from '../i18n/context.js';
import { failureReasonLabel } from '../i18n/failure.js';

// ---------------------------------------------------------------------------
// Home (UI structure spec section 7): attention first, because it is the
// only content on this screen that expires, then the project operations
// list. Phase 1 ships two attention reasons -- `needs_review` and
// `reconnect-required` (whole-branch review finding C1) -- see the task-19
// report for why the remaining reference categories (held, failed, daily
// cap, ...) are out of scope until automation modes and posting slots
// exist.
// ---------------------------------------------------------------------------

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: HomeResponse };

// Attention items and connections now carry a real `platform` ('x' |
// 'pinterest') rather than being X-only -- this mirrors review.tsx's own
// inline label, kept here rather than imported since it is one ternary and
// route files do not share UI helpers.
const platformLabel = (platform: 'x' | 'pinterest'): string => (platform === 'pinterest' ? 'Pinterest' : 'X');

const AttentionRow = ({ item }: { item: AttentionItem }): React.JSX.Element => {
  const dict = useDictionary();

  if (item.reason === 'reconnect-required') {
    return (
      <>
        <div className="row-t" style={{ padding: 'var(--sp-3) var(--sp-4)', gap: 'var(--sp-4)' }}>
          <div className="grow stack-1">
            <div className="row wrap">
              <span className="chip chip--danger">{dict.states.failed}</span>
              <span className="t-label">{dict.attentionReasons.reconnectionRequired}</span>
            </div>
            <p className="t-body strong">
              {item.projectName} — {platformLabel(item.platform)} @{item.connectionHandle}
            </p>
          </div>
          <div className="row" style={{ flex: '0 0 auto' }}>
            <Link className="btn" to={`/projects/${item.projectId}`}>{dict.actions.reconnect}</Link>
          </div>
        </div>
        <hr className="rule" />
      </>
    );
  }

  // An approved post that did not go out. Ranked by the route above a post
  // still waiting for approval, and given the danger chip rather than the
  // attention one: the operator already decided about this post once, and
  // the platform overruled them. The reason comes from the same mapping the
  // review screen uses, so the two screens cannot describe it differently.
  if (item.reason === 'publication_failed') {
    return (
      <>
        <div className="row-t" style={{ padding: 'var(--sp-3) var(--sp-4)', gap: 'var(--sp-4)' }}>
          <div className="grow stack-1">
            <div className="row wrap">
              <span className="chip chip--danger">{dict.states.failed}</span>
              <span className="t-label">{failureReasonLabel(dict, item.category)}</span>
            </div>
            <p className="t-body strong">{item.textExcerpt}</p>
            <p className="t-meta">
              {item.projectName}
              {item.campaignName ? ` — ${item.campaignName}` : ''}
              {item.connectionHandle ? ` — ${platformLabel(item.platform)} @${item.connectionHandle}` : ''}
            </p>
          </div>
          <div className="row" style={{ flex: '0 0 auto' }}>
            <Link className="btn" to={`/review/${item.variantId}`}>{dict.ui.review.title}</Link>
          </div>
        </div>
        <hr className="rule" />
      </>
    );
  }

  return (
    <>
      <div className="row-t" style={{ padding: 'var(--sp-3) var(--sp-4)', gap: 'var(--sp-4)' }}>
        <div className="grow stack-1">
          <div className="row wrap">
            <span className="chip chip--attention">{dict.states.needsReview}</span>
            <span className="t-label">{dict.attentionReasons.waitingForApproval}</span>
          </div>
          <p className="t-body strong">{item.textExcerpt}</p>
          <p className="t-meta">
            {item.projectName}
            {item.campaignName ? ` — ${item.campaignName}` : ''}
            {item.connectionHandle ? ` — ${platformLabel(item.platform)} @${item.connectionHandle}` : ''}
          </p>
        </div>
        <div className="row" style={{ flex: '0 0 auto' }}>
          <Link className="btn" to={`/review/${item.variantId}`}>{dict.ui.review.title}</Link>
        </div>
      </div>
      <hr className="rule" />
    </>
  );
};

// Every platform gets its own chip -- `connections` holds one entry per
// platform actually connected (a platform absent from the array reads as
// not connected), replacing the old X-only `xConnectionState` scalar (see
// apps/worker/src/api/home.ts's own note on why a single scalar could not
// represent an X connection and a Pinterest connection in different states
// on the same project).
const CONNECTION_PLATFORMS = ['x', 'pinterest'] as const;

const ProjectTableRow = ({ project }: { project: ProjectRow }): React.JSX.Element => {
  const dict = useDictionary();

  return (
    <tr>
      <td>
        <div className="stack-1">
          <span className="strong">{project.name}</span>
          {project.website && <span className="mono-meta">{project.website}</span>}
        </div>
      </td>
      <td>
        <div className="row wrap" style={{ gap: 'var(--sp-2)' }}>
          {CONNECTION_PLATFORMS.map((platform) => {
            const state = project.connections.find((c) => c.platform === platform)?.state ?? null;
            const connected = state === 'connected';
            return (
              <span key={platform} className={connected ? 'chip chip--done' : 'chip'}>
                {platformLabel(platform)}: {connected ? dict.ui.project.connected : dict.ui.home.notConnected}
              </span>
            );
          })}
        </div>
      </td>
      <td className="num">{project.attentionCount}</td>
      <td><span className="t-meta">{dict.automationModes.manual}</span></td>
      <td><Link className="btn btn--ghost" to={`/projects/${project.id}`}>{dict.ui.home.open}</Link></td>
    </tr>
  );
};

export const HomeRoute = (): React.JSX.Element => {
  const dict = useDictionary();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [showCreateForm, setShowCreateForm] = useState(false);

  const load = (): void => {
    setState({ status: 'loading' });
    fetchHome()
      .then((data) => setState({ status: 'ready', data }))
      .catch((err: unknown) => setState({ status: 'error', message: err instanceof Error ? err.message : String(err) }));
  };

  useEffect(load, []);

  const attentionCount = state.status === 'ready' ? state.data.attention.length : 0;

  return (
    <div className="stack" style={{ gap: 'var(--sp-5)' }}>
      <div className="between">
        <div className="stack-1">
          <h1 className="t-title">{dict.ui.home.title}</h1>
          <p className="t-meta">{dict.ui.home.subtitle(attentionCount)}</p>
        </div>
        <div className="row">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => { setShowCreateForm((v) => !v); }}
          >
            {dict.ui.home.createProject}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <CreateProjectForm
          onCreated={(projectId) => { setShowCreateForm(false); navigate(`/projects/${projectId}`); }}
          onCancel={() => { setShowCreateForm(false); }}
        />
      )}

      {state.status === 'loading' && <p className="t-meta">{dict.ui.home.loading}</p>}

      {state.status === 'error' && (
        <div className="alert alert--danger">
          <div className="stack-1">
            <div className="alert__title">{dict.ui.home.loadFailed}</div>
            <button type="button" className="btn" onClick={load}>{dict.ui.home.retry}</button>
          </div>
        </div>
      )}

      {state.status === 'ready' && (
        <>
          <div className="panel">
            <div className="panel__head">
              <div className="row" style={{ gap: 'var(--sp-3)' }}>
                <h2 className="t-section">{dict.ui.home.needsAttention}</h2>
                <span className="mono-meta">{dict.ui.home.unresolvedCount(state.data.attention.length)}</span>
              </div>
            </div>
            <div className="panel__body panel__body--flush">
              {state.data.attention.length === 0 && (
                <div className="empty">
                  <div className="empty__title">{dict.ui.home.noAttention}</div>
                </div>
              )}
              {state.data.attention.map((item) => (
                <AttentionRow
                  // A project can now carry a reconnect-required item per
                  // platform (X and Pinterest independently), so the old
                  // `reconnect-${projectId}` key -- unique only when a
                  // project held at most one connection -- would collide.
                  // `needs_review`/`publication_failed` are each keyed by
                  // their own `variantId`, already unique.
                  key={item.reason === 'reconnect-required' ? `reconnect-${item.projectId}-${item.platform}` : item.variantId}
                  item={item}
                />
              ))}
            </div>
          </div>

          <div className="panel">
            <div className="panel__head">
              <h2 className="t-section">{dict.ui.home.projects}</h2>
            </div>
            <div className="panel__body panel__body--flush">
              {state.data.projects.length === 0
                ? (
                  <div className="empty">
                    <div className="empty__title">{dict.ui.home.noProjects}</div>
                    <div className="empty__body">{dict.ui.home.createProjectPrompt}</div>
                  </div>
                )
                : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{dict.ui.home.projectName}</th>
                        <th>{dict.ui.home.connectionsColumn}</th>
                        <th className="num">{dict.ui.home.attentionColumn}</th>
                        <th>{dict.ui.home.automationColumn}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.data.projects.map((project) => <ProjectTableRow key={project.id} project={project} />)}
                    </tbody>
                  </table>
                )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
